import { Component, Inject, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, merge } from 'rxjs';
import {
    updatePermPostOfferBillingInfo,
    cancelPermPostOfferBillingInfoModal
} from 'src/app/root-store/perm-post-offer-workflow-store/actions';
import {
    PermPostOfferBillingUpdatePayload,
    PostOfferBillingInformation
} from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-billing-information.model';
import { PermPostOfferOfferModel } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-offer.model';
import { checkChar, formatAndAssignCurrencyValue } from '../utils';
import { CurrencyPipe, NgIf } from '@angular/common';
import * as permPostOfferCandidateWorkflowSelectors from '../../../../root-store/perm-post-offer-workflow-store/perm-post-offer-candidate-workflow.selectors';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SendToClientInfoCardComponent } from '../send-to-client-modal/send-to-client-info-card/send-to-client-info-card.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@UntilDestroy()
@Component({
    selector: 'nova-perm-offer-billing-update-modal',
    templateUrl: './perm-offer-billing-update-modal.component.html',
    styleUrls: ['./perm-offer-billing-update-modal.component.scss'],
    providers: [CurrencyPipe],
    standalone: true,
    imports: [
        MatDialogModule,
        NgIf,
        MatProgressBarModule,
        FormsModule,
        ReactiveFormsModule,
        SendToClientInfoCardComponent,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatButtonModule
    ]
})
export class PermOfferBillingUpdateModalComponent implements OnInit {
    // Constants
    readonly labelFlex = '25';
    readonly dataFlex = '75';

    viewModel: PermPostOfferBillingUpdatePayload;
    billingUpdateForm: FormGroup;
    isLoading = true;
    isSubmitting = false;
    offerDate: Date;

    // Form Controls
    billingAmountControl: FormControl<number>;
    discountBillingAmountControl: FormControl<number>;
    startDateControl: FormControl<Date>;

    // Form Errors
    billingAmountError: string;
    startDateError: string;
    discountBillingAmountError: string;
    permPostOfferOfferModel: PermPostOfferOfferModel;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: PermPostOfferBillingUpdatePayload,
        private readonly store: Store,
        private readonly _formBuilder: FormBuilder,
        private readonly _currencyPipe: CurrencyPipe
    ) {
        this.viewModel = data;
        this.offerDate = data.offerDate;
        this.permPostOfferOfferModel = data.permPostOfferOfferModel;

        this.buildFormControls();
        this.buildForm();
    }

    ngOnInit(): void {
        this.store
            .select(permPostOfferCandidateWorkflowSelectors.selectPostOfferBillingUpdateModalIsLoading)
            .pipe(
                filter((r) => r !== undefined),
                untilDestroyed(this)
            )
            .subscribe((loadingResult) => {
                this.isLoading = loadingResult.isLoading;
                if (!loadingResult.isLoading) {
                    this.initializeForm();
                }

                if (!loadingResult.isLoading) {
                    this.billingUpdateForm?.enable();
                } else {
                    this.billingUpdateForm?.disable();
                }
            });
    }

    initializeForm(): void {
        this.startFormControlListeners();
        if (this.viewModel.postOfferBillingInformation.billingAmount) {
            this.billingAmountControl?.setValue(this.viewModel.postOfferBillingInformation.billingAmount);
        }
        if (this.viewModel.postOfferBillingInformation.discountBillingAmount) {
            this.discountBillingAmountControl?.setValue(
                this.viewModel.postOfferBillingInformation.discountBillingAmount
            );
        }
        if (this.viewModel.postOfferBillingInformation.startDate) {
            this.startDateControl?.setValue(this.viewModel.postOfferBillingInformation.startDate);
        }
    }

    buildFormControls(): void {
        this.billingAmountControl = this._formBuilder.control<number>(null, [Validators.required, Validators.min(0)]);
        this.discountBillingAmountControl = this._formBuilder.control<number>(null, [
            Validators.required,
            Validators.min(0)
        ]);
        this.startDateControl = this._formBuilder.control<Date>(null, [Validators.required, this.startDateValidator]);
    }

    buildForm(): void {
        const formControls: Record<string, AbstractControl> = {
            billingAmount: this.billingAmountControl,
            startDate: this.startDateControl,
            discountBillingAmount: this.discountBillingAmountControl
        };

        this.billingUpdateForm = this._formBuilder.group(formControls);
    }

    startDateValidator = (control: FormControl<Date>): { invalidStartDate: boolean } => {
        const startDate = new Date(control.value);

        if (startDate && this.offerDate && startDate.getTime() < new Date(this.offerDate).getTime()) {
            return { invalidStartDate: true };
        }
        return null;
    };

    checkChar(e: KeyboardEvent): boolean {
        return checkChar(e);
    }

    onBlur(event: Event): boolean {
        return formatAndAssignCurrencyValue(event, this._currencyPipe);
    }

    onSubmit(): void {
        if (this.billingUpdateForm.valid) {
            this.isSubmitting = true;

            const data = {
                permanentJobOfferId: this.viewModel.postOfferBillingInformation.permanentJobOfferId,
                billingAmount: this.billingAmountControl.value,
                discountBillingAmount: this.discountBillingAmountControl.value,
                startDate: this.startDateControl.value
            } satisfies PostOfferBillingInformation;

            this.store.dispatch(updatePermPostOfferBillingInfo({ model: data }));
        }
    }

    closeDialog(): void {
        this.store.dispatch(cancelPermPostOfferBillingInfoModal());
    }

    startFormControlListeners(): void {
        this.listenToBillingAmountChanges();

        this.listenToDiscountBillingAmountChanges();

        this.listenToStartDateChanges();
    }

    listenToBillingAmountChanges(): void {
        merge(this.billingAmountControl.statusChanges, this.billingAmountControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.billingAmountError = this.checkErrors(this.billingAmountControl)));
    }

    listenToDiscountBillingAmountChanges(): void {
        merge(this.discountBillingAmountControl.statusChanges, this.discountBillingAmountControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.discountBillingAmountError = this.checkErrors(this.discountBillingAmountControl)));
    }

    listenToStartDateChanges(): void {
        merge(this.startDateControl.statusChanges, this.startDateControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.startDateError = this.checkErrors(this.startDateControl)));
    }

    checkErrors(control: FormControl): string {
        if (control.hasError('required')) {
            return 'You must enter a value';
        } else if (control.hasError('min')) {
            return 'Cannot be less than zero';
        } else if (control.hasError('invalidStartDate')) {
            return 'Cannot be before offer date';
        } else {
            return '';
        }
    }
}
